/**
 * Color By DENSITY / VMG legend entry: "LOW [ramp] HIGH" in place of group swatches (those modes are
 * per-chart ramps drawn by AdvancedScatter, so the page has no groups). Shared by the Performance and
 * Fleet legends.
 */
import * as d3 from "d3";
import { PERFORMANCE_DENSITY_COLOR, PERFORMANCE_VMG_COLOR } from "../../utils/colorScale";
import { densityHeatColor, vmgHeatColor } from "../../utils/scatterColors";

const RAMP_BAR_WIDTH = 80;
/** Width of the whole entry: "LOW" + gap + bar + gap + "HIGH" (7px per character). */
export const RAMP_LEGEND_ITEM_WIDTH = 3 * 7 + 8 + RAMP_BAR_WIDTH + 8 + 4 * 7;
const RAMP_STOPS = [0, 0.25, 0.5, 0.75, 1] as const;

export interface LegendRamp {
    colorAt: (t: number) => string;
    title: string;
}

/** Ramps for the color-by modes that have no groups, keyed by color-by value. */
const LEGEND_RAMPS: Record<string, LegendRamp> = {
    [PERFORMANCE_DENSITY_COLOR]: {
        colorAt: densityHeatColor,
        title: "Point density: how crowded each point's part of the chart is",
    },
    [PERFORMANCE_VMG_COLOR]: {
        colorAt: vmgHeatColor,
        title: "Average VMG % of target: blue lowest, red highest, scaled per chart",
    },
};

export function legendRampFor(color: string | undefined | null): LegendRamp | undefined {
    return color ? LEGEND_RAMPS[color] : undefined;
}

/** Draws a LOW → HIGH ramp with its left edge at `x`, vertically centred on `cy`. */
export function appendLegendRamp(
    legend: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    gradientId: string,
    x: number,
    cy: number,
    ramp: LegendRamp
): void {
    const gradient = legend.append("defs").append("linearGradient")
        .attr("id", gradientId)
        .attr("x1", "0%").attr("x2", "100%").attr("y1", "0%").attr("y2", "0%");
    RAMP_STOPS.forEach((t) => {
        gradient.append("stop")
            .attr("offset", `${t * 100}%`)
            .attr("stop-color", ramp.colorAt(t));
    });
    legend.append("text")
        .attr("transform", `translate(${x},${cy + 5})`)
        .attr("font-size", "12px")
        .attr("class", "legend-text")
        .text("LOW");
    const barX = x + 3 * 7 + 8;
    legend.append("rect")
        .attr("x", barX).attr("y", cy - 5)
        .attr("width", RAMP_BAR_WIDTH).attr("height", 10)
        .attr("rx", 2)
        .style("fill", `url(#${gradientId})`)
        .append("title").text(ramp.title);
    legend.append("text")
        .attr("transform", `translate(${barX + RAMP_BAR_WIDTH + 8},${cy + 5})`)
        .attr("font-size", "12px")
        .attr("class", "legend-text")
        .text("HIGH");
}
